/* OrganisationInfrastructurePage.jsx — Multi-office deployment page.
 *
 * Houses Organisation Health, Permission Matrix, Workflow Templates,
 * Operational Policies, and Rollout Progress in one calm, scalable
 * configuration surface.
 */
import React, { useEffect, useState } from "react";
import {
  Network, Workflow, ShieldCheck, Settings2, CheckCircle2, Circle,
  Users, BookOpen, Sliders,
} from "lucide-react";
import { api } from "../lib/api";
import { OrganisationHealthPanel } from "../components/mission-control/OrganisationHealthPanel";

/* ─── Hero ─────────────────────────────────────────────────────── */
const Hero = ({ summary, narrative }) => (
  <header
    className="relative overflow-hidden rounded-3xl mb-7 px-6 sm:px-9 lg:px-10 py-7 sm:py-9"
    data-testid="organisation-page-hero"
    style={{
      background: "linear-gradient(135deg, #07091A 0%, #1A1F44 45%, #2A1F68 100%)",
      boxShadow: "0 30px 80px -36px rgba(15,23,42,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)",
    }}
  >
    <span aria-hidden className="absolute -top-16 -left-12 w-[360px] h-[360px] rounded-full blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.40), transparent 65%)" }} />
    <div className="relative">
      <div className="inline-flex items-center gap-2 mb-3">
        <Network size={13} className="text-violet-300" strokeWidth={2.2} />
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-violet-200/90">
          Organisation Infrastructure
        </p>
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.05] mb-3"
          data-testid="organisation-page-title">
        Scalable Operations
      </h1>
      <p className="text-[14px] sm:text-[15px] text-white/65 leading-relaxed max-w-2xl">
        Configure offices, teams, roles, workflows and operational policies — calmly,
        at any scale.
      </p>
      {narrative ? (
        <p className="mt-4 text-[12.5px] text-white/55 leading-relaxed max-w-2xl">
          {narrative}
        </p>
      ) : null}
    </div>
  </header>
);

const SectionHead = ({ icon: Icon, eyebrow, title }) => (
  <div className="flex items-center gap-2.5 mb-3.5">
    <div className="w-7 h-7 rounded-md inline-flex items-center justify-center"
      style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}>
      <Icon size={13} strokeWidth={2.3} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">{eyebrow}</p>
      <h2 className="text-[13.5px] font-semibold text-slate-900 truncate">{title}</h2>
    </div>
  </div>
);

/* ─── Permission Matrix Card ───────────────────────────────────── */
const PermissionMatrix = ({ matrix }) => (
  <section className="mb-7" data-testid="organisation-permissions">
    <SectionHead icon={ShieldCheck} eyebrow="Role + Permission Matrix"
      title="Visibility and authority by role" />
    <div className="gp-card p-4">
      <div className="space-y-1.5">
        {(matrix || []).map((row) => (
          <div key={row.role} className="flex items-start gap-3 px-2 py-1.5 rounded-md hover:bg-slate-50/70"
               data-testid={`organisation-role-${row.role}`}>
            <div className="w-24 sm:w-32 shrink-0">
              <p className="text-[12.5px] font-semibold text-slate-900 truncate">{row.label}</p>
              <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">
                Tier {row.tier}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {(row.permissions || []).map((p) => (
                <span key={p}
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    background: "rgba(99,102,241,0.08)",
                    color: "#4338CA",
                    border: "1px solid rgba(99,102,241,0.18)",
                  }}>
                  {p.replace(/_/g, " ")}
                </span>
              ))}
              {(row.permissions || []).length === 0 ? (
                <span className="text-[10.5px] text-slate-400 italic">view-only</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Workflow Templates Card ──────────────────────────────────── */
const WorkflowTemplates = ({ templates }) => (
  <section className="mb-7" data-testid="organisation-workflows">
    <SectionHead icon={Workflow} eyebrow="Workflow Template Library"
      title="Reusable operational playbooks" />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {(templates || []).map((t) => (
        <div key={t.key} className="gp-card p-4" data-testid={`organisation-template-${t.key}`}>
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700 mb-1">
            Template
          </p>
          <p className="text-[13px] font-semibold text-slate-900 mb-1.5">{t.label}</p>
          <p className="text-[11.5px] text-slate-600 leading-snug mb-3">{t.description}</p>
          <div className="space-y-1">
            {(t.milestones || []).map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
                <p className="text-[11.5px] text-slate-700">{m}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ─── Policy Panel ─────────────────────────────────────────────── */
const PolicyPanel = ({ policies }) => {
  if (!policies) return null;
  const sections = [
    ["escalation",     "Escalation thresholds"],
    ["automation",     "Automation policy"],
    ["forecasting",    "Forecasting tolerances"],
    ["notifications",  "Notification behaviour"],
  ];
  return (
    <section className="mb-7" data-testid="organisation-policies">
      <SectionHead icon={Sliders} eyebrow="Operational Policy"
        title="Effective organisation-level settings" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sections.map(([key, label]) => (
          <div key={key} className="gp-card p-4" data-testid={`organisation-policy-${key}`}>
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700 mb-2">
              {label}
            </p>
            <div className="space-y-1.5">
              {Object.entries(policies[key] || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <p className="text-[11.5px] text-slate-700 truncate flex-1">
                    {k.replace(/_/g, " ")}
                  </p>
                  <p className="text-[12px] font-semibold tabular-nums text-slate-900 shrink-0 ml-2">
                    {typeof v === "boolean" ? (v ? "On" : "Off")
                     : Array.isArray(v) ? v.join(" – ")
                     : String(v)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Document Requirements Card ───────────────────────────────── */
const DocRequirements = ({ policies }) => {
  if (!policies?.documents) return null;
  const sides = [
    ["required_buyer",  "Buyer requirements"],
    ["required_seller", "Seller requirements"],
  ];
  return (
    <section className="mb-7" data-testid="organisation-doc-requirements">
      <SectionHead icon={BookOpen} eyebrow="Document Standards"
        title="Required documents per party" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sides.map(([k, label]) => (
          <div key={k} className="gp-card p-4" data-testid={`organisation-doc-${k}`}>
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700 mb-2">
              {label}
            </p>
            <div className="space-y-1">
              {(policies.documents[k] || []).map((d) => (
                <div key={d} className="flex items-center gap-2">
                  <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                  <p className="text-[11.5px] text-slate-700 capitalize">{d.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Rollout Progress Per Office ──────────────────────────────── */
const RolloutProgress = ({ offices }) => {
  if (!offices?.length) return null;
  return (
    <section className="mb-7" data-testid="organisation-rollout">
      <SectionHead icon={Settings2} eyebrow="Office Rollout"
        title="Deployment progress per office" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {offices.map((o) => (
          <div key={o.office_id} className="gp-card p-4"
               data-testid={`organisation-rollout-${o.office_id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[12.5px] font-semibold text-slate-900 truncate">{o.name}</p>
              <p className="text-[12px] font-semibold tabular-nums text-violet-700">
                {o.rollout_pct}%
              </p>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden mb-3"
              style={{ background: "rgba(148,163,184,0.12)" }}>
              <div className="h-full" style={{
                width: `${o.rollout_pct}%`,
                background: "linear-gradient(90deg, #818CF8 0%, #10B981 100%)",
                transition: "width 320ms cubic-bezier(0.4,0,0.2,1)",
              }} />
            </div>
            <div className="space-y-1">
              {(o.rollout_steps || []).map((s) => (
                <div key={s.key} className="flex items-center gap-2"
                     data-testid={`organisation-step-${o.office_id}-${s.key}`}>
                  {s.complete ? (
                    <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                  ) : (
                    <Circle size={11} className="text-slate-300 shrink-0" />
                  )}
                  <p className={`text-[11.5px] ${s.complete ? "text-slate-800" : "text-slate-500"} capitalize`}>
                    {s.key.replace(/_/g, " ")}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Page
   ─────────────────────────────────────────────────────────────────── */
export default function OrganisationInfrastructurePage({ embedded = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data: resp } = await api.get("/api/organisation/overview");
        if (alive) setData(resp?.organisation || null);
      } catch (err) {
        if (alive) setError(err?.response?.status === 403
          ? "Organisation Infrastructure is available to admin / director / manager roles only."
          : "Could not load Organisation Infrastructure.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const wrapperClass = embedded
    ? "min-w-0"
    : "md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen px-5 sm:px-7 lg:px-9 pt-6 pb-12 overflow-x-hidden";

  return (
    <div className={wrapperClass}
         data-testid="organisation-page">
      {error ? (
        <div className="gp-card p-6 text-center max-w-xl mx-auto" data-testid="organisation-error">
          <Network size={20} className="text-violet-500 mx-auto mb-3" />
          <p className="text-[13.5px] font-semibold text-slate-900 mb-1">{error}</p>
        </div>
      ) : loading ? (
        <div className="gp-card p-6 cf-pulse">
          <div className="h-3 w-40 bg-slate-100 rounded mb-3" />
          <div className="h-4 w-72 bg-slate-100 rounded mb-2" />
        </div>
      ) : data ? (
        <>
          <Hero summary={data.summary} narrative={data.narrative} />
          <OrganisationHealthPanel />
          <RolloutProgress offices={data.health?.offices || []} />
          <PermissionMatrix matrix={data.permission_matrix} />
          <WorkflowTemplates templates={data.workflow_templates} />
          <PolicyPanel policies={data.policies} />
          <DocRequirements policies={data.policies} />
        </>
      ) : null}
    </div>
  );
}
